/**
 * Global usage status management
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getUsageStatus } from '../services/usageTrackingSimple';
import { type UsageStatus } from '../config/usageLimits';
import { logger } from '../utils/logger';

interface UsageContextType {
  usageStatus: UsageStatus | null;
  loading: boolean;
  refreshUsage: () => Promise<void>;
}

const UsageContext = createContext<UsageContextType | undefined>(undefined);

interface UsageProviderProps {
  children: ReactNode;
}

export const UsageProvider: React.FC<UsageProviderProps> = ({ children }) => {
  const [usageStatus, setUsageStatus] = useState<UsageStatus | null>(null);
  const [loading, setLoading] = useState(false);

  // Refresh usage status from backend
  const refreshUsage = async () => {
    setLoading(true);
    try {
      const status = await getUsageStatus();
      setUsageStatus(status);
      logger.log('🔄 全局使用状态已更新:', status);
    } catch (error) {
      logger.error('刷新使用次数状态失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load usage status when provider mounts
  useEffect(() => {
    refreshUsage();
  }, []);

  const value: UsageContextType = {
    usageStatus,
    loading,
    refreshUsage
  };

  return (
    <UsageContext.Provider value={value}>
      {children}
    </UsageContext.Provider>
  );
};

// Hook for accessing usage context
export const useUsageContext = (): UsageContextType => {
  const context = useContext(UsageContext);
  if (context === undefined) {
    throw new Error('useUsageContext must be used within a UsageProvider');
  }
  return context;
};
